import React, { useCallback, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Stack,
  Divider,
  MenuItem,
  useTheme,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Resolver } from "react-hook-form";
import { useDispatch } from "react-redux";
import { LoadingButton } from "@mui/lab";
import { Icon } from "@iconify/react";

import { FormProvider, RHFSelect, RHFTextField } from "@app/components";
import { useRequestState } from "@app/hooks";
import { Strings } from "@app/constants";
import { Spacing } from "@app/components/spacing";
import { addSalesPerson, customer } from "../slice";
import { selectAddSalesPerson } from "../selector";
import { addSalesPersonSchema } from "../validations";
import { IUserForm } from "../types";

interface AddSalespersonModalProps {
  open: boolean;
  onClose: () => void;
  role: any;
  isFromCar?: boolean;
}

const AddSalespersonModal: React.FC<AddSalespersonModalProps> = ({
  open,
  onClose,
  role,
  isFromCar = false,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const roleOptions = ["Agent", "Salesperson"];

  const methods = useForm<IUserForm>({
    resolver: yupResolver(
      addSalesPersonSchema,
    ) as unknown as Resolver<IUserForm>,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "",
      profileImage: null,
    },
  });

  const { handleSubmit, watch, setValue, reset } = methods;
  const profileImageFile = watch("profileImage");

  const onSubmit = (formData: IUserForm) => {
    try {
      dispatch(
        addSalesPerson({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          role: isFromCar ? "Buyer" : formData.role,
          profileImage: formData.profileImage,
        }),
      );
    } catch (error) {
      console.error("Add User failed:", error);
    }
  };

  useEffect(() => {
    if (open) {
      dispatch(customer("Customer"));
      reset();
    } else {
      reset();
    }
  }, [open]);

  const onSuccess = useCallback(() => {
    onClose();
    reset();
  }, []);

  const { loading } = useRequestState({
    stateSelector: selectAddSalesPerson,
    errorShown: true,
    successMessageShown: true,
    onSuccess: onSuccess,
  });

  const fontFamily = Strings.fontLanguage.workSans;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }

      // Validate file size (e.g., 5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size should be less than 5MB");
        return;
      }

      setValue("profileImage", file, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const handleRemoveImage = () => {
    setValue("profileImage", null, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <FormProvider methods={methods}>
        <Box sx={{ px: 3, pt: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <DialogTitle
              sx={{
                p: 0,
                fontWeight: 500,
                fontFamily: fontFamily,
                color: "#000000",
              }}
            >
              {isFromCar
                ? Strings.salesPerson.addCustomer
                : Strings.salesPerson.addSalesPerson}
            </DialogTitle>
            <IconButton onClick={onClose}>
              <CloseIcon htmlColor="#080808" />
            </IconButton>
          </Stack>
        </Box>

        <Divider />

        <DialogContent sx={{ py: 3 }}>
          <Stack spacing={3} mt={1}>
            <RHFTextField
              name="firstName"
              label="First Name"
              size="small"
              required
            />
            <RHFTextField
              name="lastName"
              label="Last Name"
              size="small"
              required
            />
            <RHFTextField name="phone" label="Phone" size="small" required />
            <RHFTextField name="email" label="Email" size="small" required />

            {!isFromCar && (
              <RHFSelect name="role" label="Role" size="small" required>
                <MenuItem value="">
                  <em>Select Role</em>
                </MenuItem>
                {roleOptions.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </RHFSelect>
            )}
          </Stack>

          <Spacing top={8} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                height: 140,
                border: "2px solid #1E91B6",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: { xs: "100%", sm: "60%", md: "40%" },
                backgroundColor: "#F7F9FB",
                cursor: "pointer",
                overflow: "hidden",
                position: "relative",
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              {profileImageFile ? (
                <>
                  <img
                    src={URL.createObjectURL(profileImageFile)}
                    alt="Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.7)",
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <Icon
                  icon="fluent:camera-add-48-filled"
                  width={40}
                  color="#1E91B6"
                />
              )}
            </Box>

            {!profileImageFile && (
              <Box sx={{ typography: "caption", color: "text.secondary" }}>
                Click to upload profile image (Max: 5MB)
              </Box>
            )}

            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              fontWeight: 700,
              px: 4,
              fontSize: "16px",
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              py: 1,
            }}
          >
            {Strings.button.close}
          </Button>

          <LoadingButton
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 4,
              fontWeight: 700,
              backgroundColor: theme.palette.primary.main,
              py: 1,
              color: "white",
            }}
            loading={loading}
            disabled={loading}
          >
            {Strings.button.continue}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export { AddSalespersonModal };
